/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import type { ThemeKey, ThemeVariantStyles } from 'Src/types/theme';
import type { ReactElement } from 'react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ESCAPE } from 'Utils/keyCodes';
import { themes } from './DropDown.themes';
type DropDownProps = {
  children: (handler: () => void, isExpanded: boolean) => ReactElement | null | undefined;
  className?: string;
  contentStyle?: React.CSSProperties;
  contentClassName?: string;
  hasCloseOnClick?: boolean;
  hasArrow?: boolean;
  isExpanded?: boolean;
  noLayout?: boolean;
  renderTrigger?: (handler: () => void, isExpanded: boolean) => ReactElement;
  rootRef?: React.MutableRefObject<HTMLElement | null>;
  theme?: ThemeKey<typeof themes>;
  onClose?: () => void;
  unmountsOnClose?: boolean;
};
const defaultProps = {
  hasArrow: true,
  isExpanded: false,
  noLayout: false,
  theme: 'default' as ThemeKey<typeof themes>,
  unmountsOnClose: false,
};
export function DropDown(rawProps: DropDownProps) {
  const props = { ...defaultProps, ...rawProps };
  const {
    children,
    className,
    contentStyle,
    contentClassName,
    hasArrow,
    hasCloseOnClick,
    noLayout,
    renderTrigger,
    theme,
    onClose,
    unmountsOnClose,
  } = props;

  const [isExpanded, setIsExpanded] = useState(props.isExpanded || false);
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isExpandedRef = useRef(isExpanded);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  const collapse = useCallback(() => {
    if (onClose) {
      onClose();
    }

    if (!unmountsOnClose) {
      setIsExpanded(false);
    }
  }, [onClose, unmountsOnClose]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === ESCAPE) {
        collapse();
      }
    },
    [collapse],
  );

  const onClickOutside = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      const isMouseButtonNotPrimary =
        'button' in e && typeof e.button === 'number' && e.button !== 0;
      if (
        !contentRef.current ||
        (target && contentRef.current.contains(target)) ||
        (target && rootRef.current?.contains(target)) ||
        (target && props.rootRef?.current?.contains(target)) ||
        isMouseButtonNotPrimary
      ) {
        return;
      }

      collapse();
    },
    [collapse, props.rootRef],
  );

  useEffect(() => {
    function addEventListeners() {
      if (typeof window !== 'undefined') {
        document.addEventListener('mouseup', onClickOutside, false);
        document.addEventListener('touchstart', onClickOutside, false);
        document.addEventListener('keydown', onKeyDown, false);
      }
    }

    function removeEventListeners() {
      if (typeof window !== 'undefined') {
        document.removeEventListener('mouseup', onClickOutside, false);
        document.removeEventListener('touchstart', onClickOutside, false);
        document.removeEventListener('keydown', onKeyDown, false);
      }
    }

    if (isExpanded) {
      addEventListeners();
    } else {
      removeEventListeners();
    }

    return removeEventListeners;
  }, [isExpanded, onClickOutside, onKeyDown]);

  useEffect(() => {
    if (props.isExpanded !== isExpandedRef.current) {
      setIsExpanded(props.isExpanded || false);
    }
  }, [props.isExpanded]);

  const toggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const { thBody } = themes({
    hasArrow,
  })[theme] as ThemeVariantStyles<typeof themes>;
  return (
    <div className={classnames('inline-block', className)} ref={rootRef}>
      {renderTrigger && renderTrigger(toggle, isExpanded)}
      {isExpanded && (
        <div
          className={classnames(
            {
              'absolute left-1/2 w-full min-w-full -translate-x-1/2 transform': !noLayout,
            },
            contentClassName,
            thBody,
          )}
          data-testid="DropDown-body"
          onClick={hasCloseOnClick ? collapse : null}
          ref={contentRef}
          style={contentStyle}
        >
          {children(toggle, isExpanded)}
        </div>
      )}
    </div>
  );
}
